import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import KpiCard from '../components/KpiCard';
import SortableTh from '../components/SortableTh';
import TablePagination from '../components/TablePagination';
import CalendarioLogistica from '../components/CalendarioLogistica';
import { fmtMoney, fmtDate } from '../lib/fmt';
import { openPdfFromApi } from '../lib/pdf';
import { compareValues, paginateRows } from '../lib/table';
import type { MondayConfigOut, PaginatedResponse, PedidoOut, RecebimentoPedidoOut } from '../types/api';

const LOG_COLOR: Record<string,string> = {
  'Aguardando agendamento': 'bg-amber-500/20 text-amber-300',
  'Agendado':                'bg-mbc-blue/25 text-mbc-bluel',
  'Em trânsito':             'bg-sky-500/20 text-sky-300',
  'Recebido parcial':        'bg-emerald-500/15 text-emerald-300',
  'Recebido total':          'bg-emerald-500/40 text-emerald-100',
  'Recebido':                'bg-emerald-500/20 text-emerald-300',
};

type Tab = 'pendentes'|'agendados'|'recebidos'|'todos'|'calendario';
const TABS: {key:Tab;label:string}[] = [
  {key:'pendentes',label:'Pendentes'},
  {key:'agendados',label:'Agendados'},
  {key:'recebidos',label:'Recebidos'},
  {key:'todos',label:'Todos'},
  {key:'calendario',label:'📅 Calendário'},
];

const APROVADO_OU_PAGO = new Set(['Aprovado', 'Pago parcial', 'Pago total']);

const fmtDateInput = (d:Date) => d.toISOString().slice(0,16);

export default function Logistica() {
  const qc = useQueryClient();
  const [tab,setTab] = useState<Tab>('pendentes');
  const [agendarId,setAgendarId] = useState<number|null>(null);
  const [dataEntrega,setDataEntrega] = useState(fmtDateInput(new Date()));
  const [err,setErr] = useState<string|null>(null);
  const [mondayMsg,setMondayMsg] = useState<string|null>(null);
  const [sortBy, setSortBy] = useState<'numero' | 'fornecedor' | 'valor_total' | 'condicao' | 'data_entrega_agendada' | 'days' | 'status_log' | 'data_recebimento'>('data_entrega_agendada');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [recebimentoModalPedido, setRecebimentoModalPedido] = useState<PedidoOut | null>(null);
  const [quantidadeRecebida, setQuantidadeRecebida] = useState('');
  const [recebimentoObs, setRecebimentoObs] = useState('');
  const pageSize = 15;

  const isRecebido = (p: PedidoOut) => ['Recebido parcial', 'Recebido total'].includes(p.status_log ?? '') || p.status_po === 'Recebido';
  const canSchedule = (p: PedidoOut) => p.status_po !== 'Recebido' && !isRecebido(p);
  const getLogStatus = (p: PedidoOut) => p.status_log || 'Aguardando agendamento';

  const {data:pedidosPage,isLoading} = useQuery<PaginatedResponse<PedidoOut>>({
    queryKey:['pedidos-log'],
    queryFn:()=>api.get<PaginatedResponse<PedidoOut>>('/pedidos?limit=500').then(r=>r.data),
  });
  const pedidos = pedidosPage?.items ?? [];

  const {data:mondayCfg} = useQuery<MondayConfigOut|null>({
    queryKey:['monday-config'],
    queryFn:()=>api.get<MondayConfigOut>('/monday/config').then(r=>r.data).catch(()=>null),
  });
  const { data: recebimentosPedido = [] } = useQuery<RecebimentoPedidoOut[]>({
    queryKey: ['pedido-recebimentos', recebimentoModalPedido?.id],
    enabled: Boolean(recebimentoModalPedido?.id),
    queryFn: () => api.get<RecebimentoPedidoOut[]>(`/pedidos/${recebimentoModalPedido!.id}/recebimentos`).then(r => r.data),
  });

  const mondaySyncMut = useMutation({
    mutationFn:()=>api.post<{pushed:number;errors:number}>('/monday/sync'),
    onSuccess:(r)=>{ qc.invalidateQueries({queryKey:['pedidos-log']}); setMondayMsg(`✓ ${r.data.pushed} pedido(s) enviados ao Monday.`); },
    onError:()=>setMondayMsg('Erro ao sincronizar com Monday.com.'),
  });

  const filterFn = (t:Tab, p:PedidoOut) => {
    if(t==='pendentes') return APROVADO_OU_PAGO.has(p.status_po) && getLogStatus(p)!=='Agendado' && !isRecebido(p);
    if(t==='agendados') return p.status_log==='Agendado' && p.status_po!=='Recebido';
    if(t==='recebidos') return isRecebido(p);
    if(t==='calendario') return false;
    return APROVADO_OU_PAGO.has(p.status_po) || isRecebido(p) || !!p.status_log;
  };
  const filtered = pedidos.filter(p=>filterFn(tab,p));
  const sorted = [...filtered].sort((a, b) => {
    const todayBase = new Date(); todayBase.setHours(0, 0, 0, 0);
    const daysA = a.data_entrega_agendada ? Math.round((new Date(a.data_entrega_agendada).getTime() - todayBase.getTime()) / 86400000) : null;
    const daysB = b.data_entrega_agendada ? Math.round((new Date(b.data_entrega_agendada).getTime() - todayBase.getTime()) / 86400000) : null;
    const av = sortBy === 'days' ? daysA : sortBy === 'status_log' ? getLogStatus(a) : a[sortBy];
    const bv = sortBy === 'days' ? daysB : sortBy === 'status_log' ? getLogStatus(b) : b[sortBy];
    return compareValues(av, bv, sortDir);
  });
  const paged = paginateRows(sorted, page, pageSize);

  const agendarMut = useMutation({
    mutationFn:({id,data}:{id:number;data:string})=>api.post<PedidoOut>(`/pedidos/${id}/agendar`,{data_entrega_agendada:data}),
    onSuccess:()=>{ qc.invalidateQueries({queryKey:['pedidos-log']}); setAgendarId(null); },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });
  const receberMut = useMutation({
    mutationFn:(id:number)=>api.post<PedidoOut>(`/pedidos/${id}/receber`),
    onSuccess:(r)=>{
      qc.invalidateQueries({queryKey:['pedidos-log']});
      qc.invalidateQueries({queryKey:['pedido-recebimentos', r.data.id]});
      setRecebimentoModalPedido(r.data);
      setQuantidadeRecebida('');
      setRecebimentoObs('');
    },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });
  const receberParcialMut = useMutation({
    mutationFn:({id, quantidade, observacoes}:{id:number; quantidade:number; observacoes?:string})=>
      api.post<PedidoOut>(`/pedidos/${id}/receber-parcial`, { quantidade_recebida: quantidade, observacoes }),
    onSuccess:(r)=>{
      qc.invalidateQueries({queryKey:['pedidos-log']});
      qc.invalidateQueries({queryKey:['pedido-recebimentos', r.data.id]});
      setRecebimentoModalPedido(r.data);
      setQuantidadeRecebida('');
      setRecebimentoObs('');
    },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });
  const estornarMut = useMutation({
    mutationFn:(id:number)=>api.post<PedidoOut>(`/pedidos/${id}/estornar-recebimento`),
    onSuccess:(r)=>{
      qc.invalidateQueries({queryKey:['pedidos-log']});
      qc.invalidateQueries({queryKey:['pedido-recebimentos', r.data.id]});
      setRecebimentoModalPedido(r.data);
      setQuantidadeRecebida('');
      setRecebimentoObs('');
    },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });
  const estornarMovimentoMut = useMutation({
    mutationFn:({recebimentoId}:{recebimentoId:number})=>api.delete<PedidoOut>(`/pedidos/recebimentos/${recebimentoId}`),
    onSuccess:(r, { recebimentoId })=>{
      qc.invalidateQueries({queryKey:['pedidos-log']});
      qc.setQueryData(
        ['pedido-recebimentos', r.data.id],
        (old: RecebimentoPedidoOut[] | undefined) => old?.filter(rec => rec.id !== recebimentoId) ?? [],
      );
      setRecebimentoModalPedido(r.data);
      setQuantidadeRecebida('');
      setRecebimentoObs('');
    },
    onError:(e:any)=>setErr(e?.response?.data?.detail||String(e)),
  });

  const logPedidos = pedidos.filter(p=>APROVADO_OU_PAGO.has(p.status_po) || isRecebido(p) || !!p.status_log);
  const aguardAgend = logPedidos.filter(p=>!p.status_log||p.status_log==='Aguardando agendamento').length;
  const agendados   = logPedidos.filter(p=>p.status_log==='Agendado').length;
  const emTransito  = logPedidos.filter(p=>p.status_log==='Em trânsito').length;
  const recebidos   = logPedidos.filter(p=>isRecebido(p)).length;
  const today = new Date(); today.setHours(0,0,0,0);
  const onSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortDir(current => current === 'desc' ? 'asc' : 'desc');
      return;
    }
    setSortBy(field);
    setSortDir(field === 'numero' || field === 'fornecedor' || field === 'condicao' || field === 'status_log' ? 'asc' : 'desc');
    setPage(1);
  };

  const openRecebimentoModal = (pedido: PedidoOut) => {
    setErr(null);
    setRecebimentoModalPedido(pedido);
    setQuantidadeRecebida('');
    setRecebimentoObs('');
  };

  const openPedidoPdf = async (pedido: PedidoOut) => {
    setErr(null);
    try {
      await openPdfFromApi(`/pedidos/${pedido.id}/pdf`, `${pedido.numero}.pdf`);
    } catch (e: any) {
      setErr(e?.message || e?.response?.data?.detail || 'PDF não encontrado para este pedido.');
    }
  };

  const closeRecebimentoModal = () => {
    if (receberParcialMut.isPending || receberMut.isPending || estornarMut.isPending || estornarMovimentoMut.isPending) return;
    setRecebimentoModalPedido(null);
    setQuantidadeRecebida('');
    setRecebimentoObs('');
  };

  const confirmarRecebimentoParcial = () => {
    if (!recebimentoModalPedido) return;
    const quantidade = Number.parseInt(quantidadeRecebida, 10);
    if (!Number.isFinite(quantidade) || quantidade <= 0) {
      setErr('Informe uma quantidade recebida maior que zero.');
      return;
    }
    receberParcialMut.mutate({
      id: recebimentoModalPedido.id,
      quantidade,
      observacoes: recebimentoObs.trim() || undefined,
    });
  };

  const confirmarRecebimentoTotal = () => {
    if (!recebimentoModalPedido) return;
    receberMut.mutate(recebimentoModalPedido.id);
  };

  const quantidadeRecebidaAtual = recebimentosPedido.length > 0
    ? recebimentosPedido.reduce((s, item) => s + item.quantidade, 0)
    : (recebimentoModalPedido?.quantidade_recebida ?? 0);
  const saldoReceber = recebimentoModalPedido?.quantidade_pedido == null
    ? null
    : Math.max(0, recebimentoModalPedido.quantidade_pedido - quantidadeRecebidaAtual);

  const recebimentoModal = recebimentoModalPedido ? createPortal(
    <div className="fixed inset-0 z-[120]">
      <div className="absolute inset-0 bg-black/70" onClick={closeRecebimentoModal} />
      <div className="absolute inset-0 overflow-y-auto">
        <div className="flex min-h-full items-start justify-center p-4 pt-20 md:items-center md:p-6">
        <div className="card w-full max-w-xl rounded-2xl p-5 max-h-[calc(100vh-2rem)] overflow-y-auto md:max-h-[85vh]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-white">Movimentos de recebimento</h3>
              <p className="mt-1 text-xs text-mbc-mute">
                Pedido <span className="font-mono text-mbc-bluel">{recebimentoModalPedido.numero}</span> · lance recebimentos parciais, receba o saldo total ou estorne movimentos individualmente.
              </p>
            </div>
            <button onClick={closeRecebimentoModal} className="text-2xl leading-none text-mbc-mute hover:text-white">×</button>
          </div>

          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-mbc-soft">
              <div>{recebimentoModalPedido.fornecedor}</div>
              <div className="mt-1 text-xs text-mbc-mute">
                Status atual: {recebimentoModalPedido.status_log ?? 'Aguardando agendamento'}
              </div>
              <div className="mt-1 text-xs text-mbc-mute">
                Quantidade do pedido: <span className="text-white">{recebimentoModalPedido.quantidade_pedido ?? '—'}</span>
              </div>
              <div className="mt-1 text-xs text-mbc-mute">
                Já recebida: <span className="text-white">{quantidadeRecebidaAtual || '—'}</span>
              </div>
              <div className="mt-1 text-xs text-mbc-mute">
                Saldo a receber: <span className="text-amber-300">{saldoReceber ?? '—'}</span>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs text-mbc-mute">Quantidade recebida agora</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={quantidadeRecebida}
                  onChange={e=>setQuantidadeRecebida(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:outline-none focus:border-mbc-orange"
                  placeholder="Ex.: 12"
                />
              </label>

              <label className="block">
                <span className="text-xs text-mbc-mute">Observações</span>
                <input
                  value={recebimentoObs}
                  onChange={e=>setRecebimentoObs(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:outline-none focus:border-mbc-orange"
                  placeholder="ex: 1a entrega, ajuste, conferido"
                />
              </label>
            </div>

            <div className="rounded-xl border border-mbc-line bg-mbc-panel2/70 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-mbc-mute">Movimentos já lançados</div>
              {recebimentosPedido.length === 0 ? (
                <div className="text-xs text-mbc-mute">Nenhum recebimento registrado ainda.</div>
              ) : (
                <div className="space-y-2">
                  {recebimentosPedido.map(recebimento => (
                    <div key={recebimento.id} className="flex items-center justify-between gap-3 text-xs">
                      <span className="text-mbc-soft">
                        {fmtDate(recebimento.recebido_em)} · qtd {recebimento.quantidade}
                        {recebimento.observacoes ? ` · ${recebimento.observacoes}` : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setErr(null);
                          estornarMovimentoMut.mutate({ recebimentoId: recebimento.id });
                        }}
                        disabled={estornarMovimentoMut.isPending}
                        className="rounded-md bg-rose-500/15 px-2 py-1 text-[11px] text-rose-300 hover:bg-rose-500/25 disabled:opacity-40"
                      >
                        estornar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {err && <p className="mt-3 text-sm text-rose-300">{err}</p>}

          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button onClick={closeRecebimentoModal} className="rounded-xl border border-mbc-line bg-mbc-panel2 px-4 py-2 text-sm text-mbc-soft hover:text-white">
              Cancelar
            </button>
            {recebimentosPedido.length > 0 && (
              <button
                onClick={()=>estornarMut.mutate(recebimentoModalPedido.id)}
                disabled={estornarMut.isPending}
                className="rounded-xl bg-rose-500/15 px-4 py-2 text-sm text-rose-300 hover:bg-rose-500/25 disabled:opacity-40"
              >
                {estornarMut.isPending ? 'Estornando…' : '↺ Estornar tudo'}
              </button>
            )}
            {(saldoReceber == null || saldoReceber > 0) && (
              <button
                onClick={confirmarRecebimentoTotal}
                disabled={receberMut.isPending || (saldoReceber === 0)}
                className="rounded-xl bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40"
              >
                {receberMut.isPending ? 'Salvando…' : saldoReceber == null ? 'Marcar como total' : `Receber saldo total${saldoReceber ? ` (${saldoReceber})` : ''}`}
              </button>
            )}
            <button
              onClick={confirmarRecebimentoParcial}
              disabled={receberParcialMut.isPending || saldoReceber === 0}
              className="rounded-xl bg-mbc-orange px-4 py-2 text-sm font-semibold text-white hover:bg-mbc-orangel disabled:opacity-40"
            >
              {receberParcialMut.isPending ? 'Salvando…' : 'Registrar parcial'}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div className="space-y-6 fade-in">
      <div className="card p-5" style={{borderLeft:'4px solid #22a06b'}}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">🚚 Pedidos aprovados — agendar entrega</h3>
            <p className="text-xs text-mbc-mute mt-0.5">💡 Pedidos aprovados no financeiro são enviados automaticamente ao Monday. Use os botões de ação em cada linha para registrar trânsito, agendamento e recebimento.</p>
          </div>
          <div className="flex items-center gap-2">
            {mondayCfg && (
              <>
                <button onClick={()=>mondaySyncMut.mutate()} disabled={mondaySyncMut.isPending}
                  className="text-xs bg-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-40 text-emerald-300 px-3 py-2 rounded-lg transition-colors"
                  title="Puxar status do Monday e enviar pedidos sem item">
                  {mondaySyncMut.isPending ? '…' : '⬇ Sync Monday'}
                </button>
              </>
            )}
            <span className="text-xs text-mbc-mute">{logPedidos.length} pedido{logPedidos.length!==1?'s':''}</span>
          </div>
        </div>
        {mondayMsg && (
          <div className="mb-3 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-2 flex items-center justify-between">
            {mondayMsg}
            <button onClick={()=>setMondayMsg(null)} className="text-emerald-400 hover:text-white ml-4">✕</button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
          <KpiCard label="Aguardando agendamento" value={String(aguardAgend)} sub="a definir data"    accent="amber-300"   icon="📅" />
          <KpiCard label="Agendados"              value={String(agendados)}   sub="data confirmada"   accent="mbc-bluel"   icon="✓" />
          <KpiCard label="Em trânsito"            value={String(emTransito)}  sub="a caminho"         accent="sky-300"     icon="🚛" />
          <KpiCard label="Recebidos"              value={String(recebidos)}   sub="parcial ou total"  accent="emerald-300" icon="📦" />
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {TABS.map(({key,label})=>(
            <button key={key} onClick={()=>setTab(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${tab===key?'bg-mbc-orange text-white':'bg-mbc-panel2 border border-mbc-line text-mbc-mute hover:text-white'}`}
            >
              {label}
              {key !== 'calendario' && (
                <span className="opacity-60 ml-1">({pedidos.filter(p=>filterFn(key,p)).length})</span>
              )}
            </button>
          ))}
        </div>

        {tab === 'calendario' && <CalendarioLogistica />}

        {err&&<p className="mb-3 text-rose-300 text-sm">{err}</p>}

        {tab !== 'calendario' && (
          <>
            {agendarId!==null&&(
              <div className="border border-mbc-orange/40 bg-mbc-orange/5 rounded-xl p-4 mb-4 flex flex-wrap items-end gap-4">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-mbc-mute">Data e hora de entrega</span>
                  <input type="datetime-local" value={dataEntrega} onChange={e=>setDataEntrega(e.target.value)}
                    className="bg-mbc-panel2 border border-mbc-line rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-mbc-orange"/>
                </label>
                <button disabled={agendarMut.isPending} onClick={()=>agendarMut.mutate({id:agendarId,data:dataEntrega})}
                  className="bg-mbc-orange hover:bg-mbc-orangel disabled:opacity-40 text-white font-bold px-4 py-2 rounded-lg text-sm">Confirmar agendamento</button>
                <button onClick={()=>setAgendarId(null)} className="text-mbc-mute hover:text-white text-sm">Cancelar</button>
              </div>
            )}

            <div className="scroller">
              {isLoading ? <div className="p-8 text-center text-mbc-mute">Carregando…</div> :
              filtered.length===0 ? <div className="p-8 text-center text-mbc-mute">Nenhum pedido nesta aba.</div> : (
                <table className="dt">
                  <thead>
                    <tr>
                      <SortableTh label="Pedido" active={sortBy==='numero'} direction={sortDir} onClick={() => onSort('numero')} style={{width:110}} />
                      <SortableTh label="Fornecedor" active={sortBy==='fornecedor'} direction={sortDir} onClick={() => onSort('fornecedor')} className="text-left" style={{width:200}} />
                      <SortableTh label="Valor" active={sortBy==='valor_total'} direction={sortDir} onClick={() => onSort('valor_total')} className="num" style={{width:120}} />
                      <SortableTh label="Condição" active={sortBy==='condicao'} direction={sortDir} onClick={() => onSort('condicao')} className="text-center" style={{width:90}} />
                      <SortableTh label="Entrega prev." active={sortBy==='data_entrega_agendada'} direction={sortDir} onClick={() => onSort('data_entrega_agendada')} className="num" style={{width:120}} />
                      <SortableTh label="D-" active={sortBy==='days'} direction={sortDir} onClick={() => onSort('days')} className="num" style={{width:60}} />
                      <SortableTh label="Status log." active={sortBy==='status_log'} direction={sortDir} onClick={() => onSort('status_log')} className="text-center" style={{width:160}} />
                      <th className="text-center" style={{width:90}}>Qtd. ped.</th>
                      <th className="text-center" style={{width:90}}>Qtd. rec.</th>
                      <th className="text-center" style={{width:100}}>Qtd. líquida</th>
                      <SortableTh label="Recebido em" active={sortBy==='data_recebimento'} direction={sortDir} onClick={() => onSort('data_recebimento')} className="num" style={{width:100}} />
                      {mondayCfg && <th className="text-center" style={{width:120}}>🟦 Monday</th>}
                      <th className="text-center" style={{width:200}}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map(p=>{
                      const dt=p.data_entrega_agendada?new Date(p.data_entrega_agendada):null;
                      const days=dt?Math.round((dt.getTime()-today.getTime())/86400000):null;
                      const slog=getLogStatus(p);
                      const quantidadeLiquida =
                        p.quantidade_pedido == null
                          ? null
                          : Math.max(0, p.quantidade_pedido - (p.quantidade_recebida ?? 0));
                      return (
                        <tr key={p.id}>
                          <td>
                            <button
                              type="button"
                              onClick={() => openPedidoPdf(p)}
                              className="font-mono text-xs font-semibold text-mbc-bluel hover:text-mbc-orangel hover:underline"
                              title={p.pdf_meta?.has_pdf ? 'Abrir PDF do pedido' : 'PDF não salvo para este pedido'}
                            >
                              {p.numero}
                            </button>
                          </td>
                          <td><span className="truncate inline-block max-w-[180px]" title={p.fornecedor}>{p.fornecedor}</span></td>
                          <td className="num font-semibold">{fmtMoney(Number(p.valor_total))}</td>
                          <td className="text-center text-mbc-mute">{p.condicao??'—'}</td>
                          <td className="num text-mbc-mute">{fmtDate(p.data_entrega_agendada)}</td>
                          <td className="num">
                            {days==null?'—':<span className={days<0?'text-rose-300':days<=3?'text-amber-300':'text-mbc-soft'}>{days}d</span>}
                          </td>
                          <td className="text-center"><span className={`badge ${LOG_COLOR[slog]??'bg-slate-500/20 text-slate-300'}`}>{slog}</span></td>
                          <td className="text-center text-white">{p.quantidade_pedido ?? '—'}</td>
                          <td className="text-center text-mbc-soft">{p.quantidade_recebida ?? '—'}</td>
                          <td className="text-center text-amber-300">{quantidadeLiquida ?? '—'}</td>
                          <td className="num text-mbc-mute">{fmtDate(p.data_recebimento)}</td>
                          {mondayCfg && (
                            <td className="text-center">
                              {p.monday_item_id
                                ? <span className="text-mbc-bluel text-xs">🟦 #{p.monday_item_id.slice(-6)}</span>
                                : <span className="text-[10px] text-mbc-mute">pendente</span>
                              }
                            </td>
                          )}
                          <td className="text-center">
                            <div className="flex gap-1 justify-center flex-wrap">
                              {canSchedule(p) && (
                                <button
                                  onClick={()=>{
                                    setAgendarId(p.id);
                                    setDataEntrega(p.data_entrega_agendada ? fmtDateInput(new Date(p.data_entrega_agendada)) : fmtDateInput(new Date()));
                                    setErr(null);
                                  }}
                                  className="text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-2.5 py-1 rounded-lg"
                                >
                                  {slog==='Agendado' ? '✎ editar ag.' : '📅 agendar'}
                                </button>
                              )}
                              {(APROVADO_OU_PAGO.has(p.status_po) || isRecebido(p)) && (
                                <button
                                  onClick={()=>openRecebimentoModal(p)}
                                  disabled={receberParcialMut.isPending || receberMut.isPending || estornarMut.isPending || estornarMovimentoMut.isPending}
                                  className="text-xs px-2.5 py-1 rounded-lg disabled:opacity-40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/20"
                                >
                                  📦 movimentos
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <TablePagination total={filtered.length} page={page} pageSize={pageSize} onPageChange={setPage} />
          </>
        )}
      </div>
      {recebimentoModal}
    </div>
  );
}
